import React, { useState } from 'react'
import axios from 'axios'
import ServiceTaskForm from './ServiceTaskForm'
import moment from 'moment-timezone'
import { t } from '../../i18n'
import SectionTitle from '../shared/sectionTitle'
import { ServiceTask, RecurrenceType } from './models'
import { LocalizationContext, LocalizationContextType, DEFAULT_LOCALIZATION_CONTEXT } from '../shared/contexts'

// Built lazily so the "tomorrow" default for start_date reflects the tenant's
// local tomorrow, not the browser's.
const buildEmptyServiceTask = (tenantTz: string): ServiceTask => ({
  id: null,
  title: '',
  active: true,
  description: '',
  recurrence_rule: {
    id: null,
    recurrence_type: 'day' as RecurrenceType,
    interval: 1,
    days_of_week: [],
    start_date: moment.tz(tenantTz).add(1, 'days'),
  },
})

const ServiceTaskPage: React.FC<{
  serviceTask?: ServiceTask
  localization_context?: LocalizationContextType
}> = ({ serviceTask: initialServiceTask = null, localization_context }) => {
  const tenantTz = localization_context?.timezone || 'UTC'
  const [serviceTask, setServiceTask] = useState(initialServiceTask || buildEmptyServiceTask(tenantTz))
  const [saveSuccessful, setSaveSuccessful] = useState<boolean | null>(null)
  const [saveText, setSaveText] = useState('')

  const handleSubmit = (serviceTask, onFinished) => {
    const { recurrence_rule, ...task } = serviceTask
    const body = { service_task: { ...task, recurrence_rule_attributes: recurrence_rule } }

    const request = serviceTask.id ? updateServiceTaskRequest(body) : createServiceTaskRequest(body)
    request
      .then((resp) => {
        setSaveSuccessful(true)
        setSaveText(t('save_successful', { scope: 'service_tasks' }))
        onFinished(true, null)
        setServiceTask(resp.data)
      })
      .catch((error) => {
        setSaveSuccessful(false)
        setSaveText(t('save_failed', { scope: 'service_tasks' }))
        onFinished(false, error.response.data)
      })
  }

  const createServiceTaskRequest = (serviceTaskBody) => {
    return axios.post('/admin/service_tasks', serviceTaskBody)
  }

  const updateServiceTaskRequest = (serviceTaskBody) => {
    return axios.put(`/admin/service_tasks/${serviceTask.id}`, serviceTaskBody)
  }

  const renderSaveMessage = () => {
    if (saveText) {
      return (
        <div
          className={`alert alert-${saveSuccessful ? 'success' : 'danger'}`}
          role="alert"
        >
          {saveText}
        </div>
      )
    }
  }

  return (
    <LocalizationContext.Provider value={localization_context || DEFAULT_LOCALIZATION_CONTEXT}>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <SectionTitle rows={[!!serviceTask.id ? 'Edit': 'New', t('service_tasks.service_task')]} />
      </div>
      <div className="card card-robis">
        <div className="row">
          <div className="col-sm-12">
            {renderSaveMessage()}
            <ServiceTaskForm
              serviceTask={serviceTask}
              onSubmitForm={handleSubmit}
            />
          </div>
        </div>
      </div>
    </LocalizationContext.Provider>
  )
}

export default ServiceTaskPage
